/**
 * GH#1778 capa 4b — un crash de render atrapado por un ErrorBoundary llega a la caja
 * negra propia (POST /public/client-errors → cmpae_incidents), no sólo a la consola.
 *
 * Antes: RouteErrorBoundary sólo reportaba si había un window.Sentry, y no lo hay
 * (no existe @sentry/react en el dashboard). AppErrorBoundary no reportaba nada. Un
 * cliente veía la pantalla rota y nosotros no nos enterábamos.
 *
 * Reglas, y cada una evita un daño:
 *   - NUNCA lanza: corre dentro de componentDidCatch; si tirara, rompería la
 *     pantalla de error que el boundary está por mostrar.
 *   - El mismo crash (boundary + ruta + primera línea) se manda una vez cada 60 s.
 *   - Tope de 5 reportes por carga de página: un loop de render no inunda la API.
 *   - Ni tenant ni usuario: el servidor no los acepta del cuerpo (TENANT FIRST).
 *   - publicApi: no lleva token ni tiene el interceptor que redirige en 401.
 */
import { publicApi } from '@/config/axios.config';

export const VENTANA_DEDUP_MS = 60_000;
export const TOPE_POR_PAGINA = 5;

const ultimos = new Map<string, number>();
let enviados = 0;

export interface CrashDeRender {
  error: unknown;
  componentStack?: string | null;
  boundary: 'route' | 'app';
  moduleName?: string;
  /** Patrón de la ruta (/agencia/clientes/:id) si se conoce; si no, el pathname. */
  routePath?: string;
}

/** Sólo para tests: vuelve el estado del reportero a cero. */
export function _reiniciarReportero(): void {
  ultimos.clear();
  enviados = 0;
}

export function reportarCrashDeRender(crash: CrashDeRender): void {
  try {
    const err = crash.error as { message?: string; stack?: string; name?: string } | undefined;
    const message = `${err?.name && err?.message && !String(err.message).startsWith(err.name) ? err.name + ': ' : ''}${
      err?.message ?? String(crash.error)
    }`.slice(0, 2000);
    const route = (crash.routePath || window.location.pathname || '/').slice(0, 300);
    const clave = `${crash.boundary}|${route}|${message.split('\n')[0]}`;
    const ahora = Date.now();
    const antes = ultimos.get(clave);
    if (antes !== undefined && ahora - antes < VENTANA_DEDUP_MS) return;
    if (enviados >= TOPE_POR_PAGINA) return;
    ultimos.set(clave, ahora);
    enviados += 1;

    const release = (import.meta.env.VITE_COMMIT_SHA as string | undefined)?.slice(0, 64);
    void publicApi
      .post('/public/client-errors', {
        message,
        stack: err?.stack ? String(err.stack).slice(0, 8000) : undefined,
        componentStack: crash.componentStack ? String(crash.componentStack).slice(0, 8000) : undefined,
        route,
        module: crash.moduleName?.slice(0, 80),
        boundary: crash.boundary,
        release: release || undefined,
      })
      .catch(() => undefined);
  } catch {
    // El reporte es best-effort: la pantalla de error tiene que salir igual.
  }
}
